<script lang="ts" setup>
import DirectSalesForm from '@/domains/direct-sales/components/direct-sales-form/direct-sales-form.vue'
import { DIRECT_SALES_UTMS } from '@/domains/direct-sales/const/utm.const'
import { EUiFormColor } from '@/domains/direct-sales/const/form.const'
import { ROUTE_PATHS } from '@/utils/route'

import HomeTextWithImageColumns from '@/domains/home/components/home-textWithImageColumns/home-textWithImageColumns.vue'
import HomeTextWithImageAndIconList from '@/domains/home/components/home-textWithImageAndIconList/home-textWithImageAndIconList.vue'
import useStaticData from '@/composables/useStaticData'
import { adaptNumbers } from '@/domains/home/composables/useAdaptNumbers'
import UiPrizes from '@/components/UiPrizes/UiPrizes.vue'
import { useHomePage } from '@/domains/home/composables/useHomePage'
import { useRouteLink } from '@/composables/useRouteLink'

const { routeLink } = useRouteLink()
const { t, locale } = useI18n()
const { content, elements } = await useHomePage()

const data = computed(() => {
  return {
    content: content.value,
  }
})

useHead(() => ({
  title: t('pageTitles.home.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.home.meta'),
    },
  ],
  link: [
    {
      hid: 'favicon',
      rel: 'icon',
      type: 'image/x-icon',
      href: '/favicon.ico',
    },
    {
      rel: 'canonical',
      href: t(`pageTitles.home.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.home.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.home.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.home.canonical.pl'),
    },
  ],
}))

const { getItems } = useStaticData()
const { getCampaignToken } = useMapDataNewsletter()

const articlesTitle = t('articles.title')

const homeItems = await getItems(['iconList', 'boxes'])

const prizeArray = computed(() => [
  {
    text: t('home.prize.text1Top'),
    subtext: t('home.prize.text1Bottom'),
  },

  {
    text: t('home.prize.text2Top'),
    subtext: t('home.prize.text2Bottom'),
  },
])

</script>

<template>
  <div>
    <Hero
      id="hero"
      desktop-image="/images/hero/benefitsystems-home-hero_d.jpg"
      mobile-image="/images/hero/benefitsystems-home-hero_m.jpg"
      :header="$t('home.hero.header')"
      :text="$t('home.hero.text')"
      :button="{
        text: $t('home.hero.buttonText'),
        to: '#formularz',
      }"
    />

    <TextWithImage
      id="wellbeing"
      :header="$t('home.section1.header')"
      :text="$t('home.section1.textFirst')"
      :text-second="$t('home.section1.textSecond')"
      :text-third="$t('home.section1.textThird')"
      :text-third-extra="$t('home.section1.textThirdExtra')"
      image="/images/image_we_1.png"
      :button="{
        text: $t('home.section1.buttonText'),
        to: routeLink(ROUTE_PATHS.SOLUTIONS),
      }"
    />

    <UiPrizes
      id="nagrody"
      :header="$t('home.prize.header')"
      :texts="prizeArray"
      :img="{
        src: '/images/prize.webp',
        srcMobile: '/images/prize-mobile.webp',
        alt: $t('home.prize.imageAlt')
      }"
    />
  
    <HomeTextWithImageColumns
      v-if="data?.content?.items.bsNumbers"
      id="multisport"
      layout="layout-1"
      :header="$t('home.section2.header')"
      :text="$t('home.section2.text')"
      image="/images/image-with-numbers.png"
      :numbers="adaptNumbers(data?.content?.items.bsNumbers.numbers)"
      layout-numbers="vertical"
      :button="{
        text: $t('home.section2.buttonText'),
        to: routeLink(ROUTE_PATHS.PRODUCTS_MULTISPORT),
      }"
    />

    <TextWithImage
      id="multilife"
      layout="layout-1"
      :list="homeItems.iconsList1"
      :header="$t('home.section3.header')"
      :text="$t('home.section3.text')"
      image="/images/image_2.png"
      :button="{
        text: $t('home.section3.buttonText'),
        to: routeLink(ROUTE_PATHS.PRODUCTS_MULTILIFE),
      }"
    />

    <HomeTextWithImageColumns
      id="mybenefit"
      layout="layout-1"
      :header="$t('home.section4.header')"
      :text="$t('home.section4.text')"
      image="/images/image_4.png"
      image2="/images/image_5.png"
      :list="homeItems.iconsList2"
      :button="{
        text: $t('home.section4.buttonText'),
        to: routeLink(ROUTE_PATHS.PRODUCTS_MYBENEFIT),
      }"
    />

    <TextWithBoxes
      id="dobrostan-pracownikow"
      layout="default"
      :header="$t('home.section5.header')"
      :text="$t('home.section5.text')"
      :boxes="homeItems.boxes1"
      color="gray"
    />

    <TextInBox
      id="raport-multisport-index"
      layout="default"
      :box="{
        text: $t('home.section6.text'),
        subtext: $t('home.section6.subtext'),
        image: '/images/image_6.png',
      }"
    />

    <HomeTextWithImageAndIconList
      id="rozwiazania-dla-firm"
      :header="$t('home.section7.header')"
      :subheader="$t('home.section7.subheader')"
      image="/images/image_7.png"
      :list="homeItems.iconsList3"
      :button="{
        text: $t('home.section7.buttonText'),
        to: routeLink(ROUTE_PATHS.SOLUTIONS),
      }"
    />

    <TextWithImage
      id="potrzeby-zespolu"
      layout="layout-2"
      :header="$t('home.section9.header')"
      :subheader="$t('home.section9.subheader')"
      :text="$t('home.section9.text')"
      :text-second="$t('home.section9.textSecond')"
      image="/images/image_8.png"
      icon="/b-corp-logo.svg"
    />

    <UiNewsletter
      v-if="locale === 'pl'"
      :title="$t('home.newsletter.title')"
      checkbox-name="webform[consent#m-ver#Jx]"
      :extend="$t('newsletter.consent.wellbeing.extend')"
      :campaign-token="getCampaignToken('wellbeingLab')"
      :subtitle="$t('home.newsletter.subtitle')"
      :consent="$t('newsletter.consent.wellbeing.noExtend')"
      version="primary"
      thank-you-url="/newsletter/dziekujemy"
    />

    <CardCarousel
      v-if="elements?.length"
      id="artykuly"
      :header="articlesTitle"
      :slides="elements"
      color="gray"
    />

    <DirectSalesForm
      :header="$t('home.form.header')"
      :color="EUiFormColor.GRAY"
      :utm="DIRECT_SALES_UTMS.mainPage"
      :badge="`/icons/badges/flag-4_${locale}.png`"
      :form-id="$t('home.form.id')"
    />
  </div>
</template>

<style lang="scss" scoped>
  @include atXL {
    :deep(.banner__content) {
       width: 55%;
    }
  }
</style>

